import {
  createContext,
  useCallback,
  useState,
  useContext,
  useRef,
  Dispatch,
  SetStateAction,
  ReactNode,
} from 'react';
import { IBuildindCorretor } from '../types';
import { useAuth } from './auth';
import {
  DEFAULT_SEARCH_FILTERS,
  SearchFilters,
} from '../types/searchFilters';
import { buildListBuildingParams } from '../utils/buildListBuildingParams';
import { searchParamsToFilters } from '../utils/searchUrlParams';

interface SearchContextProps {
  filters: SearchFilters;
  setFilters: Dispatch<SetStateAction<SearchFilters>>;
  updateFilter: <K extends keyof SearchFilters>(
    key: K,
    value: SearchFilters[K],
  ) => void;
  querySearch: string;
  setQuerySearch: (val: string) => void;
  searchBuildings: () => Promise<void>;
  applySearchFromUrl: (
    params: URLSearchParams,
    options?: { force?: boolean },
  ) => Promise<void>;
  loadNextPage: () => void;
  resetFilters: () => void;
  clearSearchResults: () => void;
  buildings: IBuildindCorretor[];
  buildingsTotal: IBuildindCorretor[];
  loading: boolean;
  loadingScroll: boolean;
  hasMore: boolean;
}

const SearchContext = createContext<SearchContextProps>({} as SearchContextProps);

const PAGE_SIZE = 12;

const SearchProvider = ({ children }: { children: ReactNode }) => {
  const { user, api } = useAuth();

  const [filters, setFilters] = useState<SearchFilters>(DEFAULT_SEARCH_FILTERS);
  const [querySearch, setQuerySearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingScroll, setLoadingScroll] = useState(false);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [buildings, setBuildings] = useState<IBuildindCorretor[]>([]);
  const [buildingsTotal, setBuildingsTotal] = useState<IBuildindCorretor[]>([]);
  const lastUrlKeyRef = useRef<string | null>(null);

  const updateFilter = useCallback(
    <K extends keyof SearchFilters>(key: K, value: SearchFilters[K]) => {
      setFilters((prev) => ({ ...prev, [key]: value }));
    },
    [],
  );

  const resetFilters = useCallback(() => {
    // Só limpa o formulário; a lista só muda ao clicar em Pesquisar
    setFilters({ ...DEFAULT_SEARCH_FILTERS });
    setQuerySearch('');
  }, []);

  const clearSearchResults = useCallback(() => {
    setBuildings([]);
    setBuildingsTotal([]);
    setPage(0);
    setHasMore(true);
    lastUrlKeyRef.current = null;
  }, []);

  const fetchPage = useCallback(
    async (pageIndex: number, currentFilters: SearchFilters, append: boolean) => {
      const params = buildListBuildingParams(
        {
          ...currentFilters,
          query_search: currentFilters.query_search || '',
        },
        { id_corretor: user?.id_corretor ?? '', no_address: true },
      );

      const res = await api.get(`/portal/building/list-building/${pageIndex}`, {
        params,
      });

      const data: IBuildindCorretor[] = res.data ?? [];

      if (append) {
        setBuildings(data);
        setBuildingsTotal((prev) => [...prev, ...data]);
      } else {
        setBuildings(data);
        setBuildingsTotal(data);
      }

      setHasMore(data.length >= PAGE_SIZE);
      return data;
    },
    [api, user?.id_corretor],
  );

  const searchBuildings = useCallback(async () => {
    setLoading(true);
    setBuildings([]);
    setBuildingsTotal([]);
    setHasMore(true);

    const merged: SearchFilters = {
      ...filters,
      query_search: querySearch || filters.query_search,
    };

    try {
      await fetchPage(0, merged, false);
      setPage(1);
    } catch (e) {
      console.log(e);
      setHasMore(false);
    } finally {
      setLoading(false);
    }
  }, [fetchPage, filters, querySearch]);

  const applySearchFromUrl = useCallback(
    async (params: URLSearchParams, options?: { force?: boolean }) => {
      const urlKey = params.toString();
      if (!options?.force && lastUrlKeyRef.current === urlKey) return;
      lastUrlKeyRef.current = urlKey;

      const { filters: nextFilters, querySearch: nextQuery } =
        searchParamsToFilters(params);

      setFilters(nextFilters);
      setQuerySearch(nextQuery);
      setLoading(true);
      setBuildings([]);
      setBuildingsTotal([]);
      setHasMore(true);

      const merged: SearchFilters = {
        ...nextFilters,
        query_search: nextQuery || nextFilters.query_search,
      };

      try {
        await fetchPage(0, merged, false);
        setPage(1);
      } catch (e) {
        console.log(e);
        setHasMore(false);
      } finally {
        setLoading(false);
      }
    },
    [fetchPage],
  );

  const loadNextPage = useCallback(() => {
    if (!hasMore || loadingScroll || loading) return;
    if (page <= 0 || buildings.length === 0) return;

    const nextPage = page;
    setLoadingScroll(true);

    const merged: SearchFilters = {
      ...filters,
      query_search: querySearch || filters.query_search,
    };

    fetchPage(nextPage, merged, true)
      .then(() => {
        setPage((p) => p + 1);
      })
      .catch((e) => console.log(e))
      .finally(() => setLoadingScroll(false));
  }, [
    hasMore,
    loadingScroll,
    loading,
    page,
    buildings.length,
    fetchPage,
    filters,
    querySearch,
  ]);

  return (
    <SearchContext.Provider
      value={{
        filters,
        setFilters,
        updateFilter,
        querySearch,
        setQuerySearch,
        searchBuildings,
        applySearchFromUrl,
        loadNextPage,
        resetFilters,
        clearSearchResults,
        buildings,
        buildingsTotal,
        loading,
        loadingScroll,
        hasMore,
      }}
    >
      {children}
    </SearchContext.Provider>
  );
};

function useSearch(): SearchContextProps {
  const context = useContext(SearchContext);

  if (!context) {
    throw new Error('useSearch must be used within a SearchProvider');
  }

  return context;
}

export { SearchProvider, useSearch };
